import { Button } from "@/components/ui/button";
import { scrollToForm, trackEvent } from "@/lib/tracking";
import { ArrowUpRight, PlayCircle } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import PhoneMockup from "@/components/whatsapp/PhoneMockup";

const HERO_PHONES = [
  {
    title: "New Enquiry",
    messages: [
      { from: "user", text: "Hi, I need a quote." },
      {
        from: "bot",
        text: "Hi! I can help. What service are you interested in?",
        options: ["Installation", "Repairs", "Maintenance", "Other"],
      },
    ],
  },
  {
    title: "Lead Qualification",
    messages: [
      { from: "bot", text: "May I have your name and business name?" },
      { from: "user", text: "Lerato, Bright Interiors" },
      { from: "bot", text: "How would you like us to contact you?", options: ["WhatsApp", "Phone call", "Email"] },
    ],
  },
  {
    title: "Human Handoff",
    messages: [
      { from: "bot", text: "Thank you. Your details have been captured." },
      { from: "bot", text: "A member of our team will continue this conversation with you shortly." },
      { from: "system", text: "Assigned to Sales Team" },
    ],
    statusBadge: { label: "Qualified", subtitle: "Assigned: Sales Team", color: "#34a853" },
  },
];

export default function WhatsappHero() {
  const handleCta = () => {
    trackEvent("cta_click", { location: "wa_hero" });
    scrollToForm();
  };

  return (
    <section id="top" data-testid="wa-hero-section" className="relative pt-28 pb-20 sm:pt-32 sm:pb-28 overflow-hidden bg-mint-wash">
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.04) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse at top, black 0%, transparent 65%)",
          }}
        />
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3.5 py-1.5 text-[11px] tracking-[0.14em] uppercase text-zinc-700 font-semibold shadow-sm">
            <FaWhatsapp className="h-3.5 w-3.5 text-[#25d366]" />
            WhatsApp business automation
          </div>

          <h1
            data-testid="wa-hero-h1"
            className="font-display mt-6 text-[38px] leading-[1.03] sm:text-[52px] lg:text-[56px] font-black tracking-[-0.035em] text-zinc-900"
          >
            WhatsApp Automation Setup for South African Businesses
          </h1>

          <p data-testid="wa-hero-subheadline" className="mt-5 text-xl sm:text-2xl font-semibold text-zinc-800 max-w-2xl leading-snug">
            Respond faster. Capture leads. Automate customer conversations.
          </p>

          <p className="mt-4 text-base sm:text-lg text-zinc-600 max-w-2xl leading-relaxed">
            Rekisa helps businesses set up WhatsApp automation using Chatfuel. We create automated conversations that answer
            common questions, collect customer information, qualify enquiries and connect customers with your team when they
            need human assistance.
          </p>
          <p className="mt-3 text-sm text-zinc-500 max-w-2xl">
            Whether you receive sales enquiries, booking requests or customer support questions, we help you build a WhatsApp
            system around your business.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              onClick={handleCta}
              data-testid="wa-hero-cta-request-quote"
              className="bg-[#25d366] hover:bg-[#1ebe5d] text-[#04220f] rounded-full whitespace-normal h-auto min-h-14 py-3 text-center px-7 text-base font-semibold shadow-[0_8px_24px_rgba(37,211,102,0.4)] transition-[background-color,box-shadow]"
            >
              Get a WhatsApp Automation Quote
              <ArrowUpRight className="ml-1 h-5 w-5" />
            </Button>
            <a
              href="#demo"
              onClick={() => trackEvent("cta_click", { location: "wa_hero_secondary", service: "whatsapp" })}
              data-testid="wa-hero-cta-how-it-works"
              className="inline-flex items-center gap-2 rounded-full h-14 px-6 border border-zinc-300 bg-white text-zinc-900 font-semibold hover:border-zinc-400 transition-colors"
            >
              <PlayCircle className="h-5 w-5 text-[#25d366]" /> See How It Works
            </a>
          </div>

          <p data-testid="wa-hero-trust" className="mt-8 text-xs sm:text-sm text-zinc-600 font-semibold flex flex-wrap gap-x-3 gap-y-1">
            <span>Implemented by Rekisa</span><span aria-hidden className="text-zinc-300">|</span>
            <span>Powered by Chatfuel</span><span aria-hidden className="text-zinc-300">|</span>
            <span>Built for South African Businesses</span>
          </p>
        </div>

        <div className="relative h-[560px] sm:h-[600px]">
          {/* Soft glow */}
          <div aria-hidden className="absolute inset-0 -z-10">
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-[#25d366]/20 blur-3xl float-y" />
          </div>
          <div className="absolute left-2 top-4 -rotate-[8deg] origin-bottom-left">
            <PhoneMockup {...HERO_PHONES[0]} businessName="Your Business" size="sm" />
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 top-0 z-10">
            <PhoneMockup {...HERO_PHONES[1]} businessName="Your Business" size="md" />
          </div>
          <div className="absolute right-2 top-4 rotate-[8deg] origin-bottom-right">
            <PhoneMockup {...HERO_PHONES[2]} businessName="Your Business" size="sm" />
          </div>
        </div>
      </div>
    </section>
  );
}
